/**
 * Re-Baseline Comparison Page - Sprint 14
 * GC-3: Re-Baseline Workflow UI
 * 
 * Side-by-side comparison of vCurrent vs Proposed
 * with approval flow to create vNext baseline
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowRight, ArrowUp, ArrowDown, CheckCircle, XCircle,
  DollarSign, Calendar, TrendingUp, TrendingDown, AlertTriangle,
  Clock, User, FileText, Loader2, History, Plus, Minus
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useCurrency } from '../utils/currencyUtils';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Delta Display Component (uses currency from hook in parent)
const DeltaDisplay = ({ current, proposed, label, format = 'currency', formatCurrency }) => {
  const delta = (proposed || 0) - (current || 0);
  const pct = current > 0 ? ((delta / current) * 100) : 0;
  const isPositive = delta > 0;
  const isNegative = delta < 0;
  
  const formatValue = (val) => {
    if (format === 'currency') return formatCurrency(val);
    if (format === 'percent') return `${val?.toFixed(1)}%`;
    return val;
  };
  
  return (
    <div className="bg-white rounded-lg border p-4">
      <div className="text-sm text-gray-500 mb-2">{label}</div>
      <div className="grid grid-cols-3 gap-4 items-center">
        <div className="text-center">
          <div className="text-xs text-gray-400 mb-1">Current</div>
          <div className="font-semibold text-gray-900">{formatValue(current)}</div>
        </div>
        
        <div className="text-center">
          <ArrowRight className="w-5 h-5 mx-auto text-gray-300" />
          <div className={`text-sm font-bold mt-1 ${
            isPositive ? 'text-red-600' : isNegative ? 'text-green-600' : 'text-gray-500'
          }`}>
            {isPositive && '+'}
            {format === 'currency' ? formatCurrency(delta) : delta.toFixed(1)}
            {format !== 'currency' && '%'}
          </div>
        </div>
        
        <div className="text-center">
          <div className="text-xs text-gray-400 mb-1">Proposed</div>
          <div className={`font-semibold ${
            isPositive ? 'text-red-600' : isNegative ? 'text-green-600' : 'text-gray-900'
          }`}>
            {formatValue(proposed)}
          </div>
        </div>
      </div>
      
      {pct !== 0 && (
        <div className={`text-center mt-2 text-xs ${
          isPositive ? 'text-red-500' : 'text-green-500'
        }`}>
          {isPositive ? <TrendingUp className="w-3 h-3 inline mr-1" /> : <TrendingDown className="w-3 h-3 inline mr-1" />}
          {pct > 0 ? '+' : ''}{pct.toFixed(1)}% variance
        </div>
      )}
    </div>
  );
};

// Severity Badge
const SeverityBadge = ({ severity }) => {
  const config = {
    low: { color: 'bg-green-100 text-green-700', label: 'Low Impact' },
    medium: { color: 'bg-amber-100 text-amber-700', label: 'Medium Impact' },
    high: { color: 'bg-orange-100 text-orange-700', label: 'High Impact' },
    critical: { color: 'bg-red-100 text-red-700', label: 'Critical Impact' }
  };
  
  const cfg = config[severity] || config.low;
  
  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium ${cfg.color}`}>
      {cfg.label}
    </span>
  );
};

// Baseline Timeline Component
const BaselineTimeline = ({ history, formatCurrency }) => {
  if (!history || history.length === 0) return null;
  
  return (
    <div className="bg-white rounded-lg border p-4">
      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <History className="w-5 h-5" />
        Baseline History
      </h3>
      
      <div className="relative">
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
        
        <div className="space-y-4">
          {history.map((entry, idx) => (
            <div key={idx} className="relative pl-10">
              <div className={`absolute left-2 w-5 h-5 rounded-full border-2 ${
                idx === 0 ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-300'
              }`} />
              
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-gray-900">
                    V{entry.version}
                    {idx === history.length - 1 && (
                      <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                        Original
                      </span>
                    )}
                    {idx === 0 && history.length > 1 && (
                      <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                        Current
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-gray-500">
                    {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : ''}
                  </span>
                </div>
                
                <div className="text-sm text-gray-600 flex items-center gap-4">
                  <span>{formatCurrency(entry.total_cost)}</span>
                  <span className="text-gray-400">|</span>
                  <span className="capitalize">{entry.trigger?.replace('_', ' ')}</span>
                </div>
                
                {entry.variance_from_previous && (
                  <div className={`text-xs mt-1 ${
                    entry.variance_from_previous.delta_cost >= 0 ? 'text-red-500' : 'text-green-500'
                  }`}>
                    {entry.variance_from_previous.delta_cost >= 0 ? '+' : ''}
                    {formatCurrency(entry.variance_from_previous.delta_cost)}
                    ({entry.variance_from_previous.variance_percentage}%)
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Proposed Changes Form
const ProposedChangesForm = ({ onAddChange, changes, onRemoveChange, formatCurrency }) => {
  const [form, setForm] = useState({
    change_type: 'financial',
    category: 'labor',
    description: '',
    delta: 0
  });
  
  const handleAdd = () => {
    if (!form.description || form.delta === 0) {
      toast.error('Please enter description and delta value');
      return;
    }
    onAddChange({ ...form });
    setForm({ change_type: 'financial', category: 'labor', description: '', delta: 0 });
  };
  
  return (
    <div className="bg-white rounded-lg border p-4">
      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Plus className="w-5 h-5" />
        Propose Changes
      </h3>
      
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label className="text-xs text-gray-500">Type</label>
          <select
            value={form.change_type}
            onChange={(e) => setForm({ ...form, change_type: e.target.value })}
            className="w-full border rounded px-2 py-1.5 text-sm mt-1"
          >
            <option value="financial">Financial</option>
            <option value="scope">Scope</option>
            <option value="schedule">Schedule</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500">Category</label>
          <select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="w-full border rounded px-2 py-1.5 text-sm mt-1"
          >
            <option value="labor">Labor</option>
            <option value="non_labor">Non-Labor</option>
            <option value="capex">CAPEX</option>
            <option value="opex">OPEX</option>
          </select>
        </div>
      </div>
      
      <div className="mb-3">
        <label className="text-xs text-gray-500">Description</label>
        <input
          type="text"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Reason for change"
          className="w-full border rounded px-2 py-1.5 text-sm mt-1"
        />
      </div>
      
      <div className="mb-3">
        <label className="text-xs text-gray-500">Delta Amount ($)</label>
        <input
          type="number"
          value={form.delta}
          onChange={(e) => setForm({ ...form, delta: parseFloat(e.target.value) || 0 })}
          placeholder="Change amount (use negative for reduction)"
          className="w-full border rounded px-2 py-1.5 text-sm mt-1"
        />
      </div>
      
      <button
        onClick={handleAdd}
        className="w-full py-2 bg-blue-50 text-blue-700 rounded font-medium hover:bg-blue-100 flex items-center justify-center gap-2"
      >
        <Plus className="w-4 h-4" />
        Add Change
      </button>
      
      {/* List of changes */}
      {changes.length > 0 && (
        <div className="mt-4 space-y-2">
          {changes.map((c, idx) => (
            <div key={idx} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
              <div className="text-sm">
                <span className="font-medium">{c.description}</span>
                <span className="text-gray-500 ml-2">
                  {c.delta >= 0 ? '+' : ''}{formatCurrency(c.delta)}
                </span>
              </div>
              <button
                onClick={() => onRemoveChange(idx)}
                className="text-red-500 hover:text-red-700"
              >
                <Minus className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Component
const ReBaselineComparison = () => {
  const [searchParams] = useSearchParams();
  const scopeId = searchParams.get('scopeId');
  const navigate = useNavigate();
  const { formatCurrency, currencySymbol } = useCurrency();
  
  const [loading, setLoading] = useState(true);
  const [currentBaseline, setCurrentBaseline] = useState(null);
  const [history, setHistory] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [proposedChanges, setProposedChanges] = useState([]);
  const [changeReason, setChangeReason] = useState('');
  const [comparing, setComparing] = useState(false);
  const [approving, setApproving] = useState(false);
  
  // Load current baseline and history
  useEffect(() => {
    const loadData = async () => {
      if (!scopeId) {
        setLoading(false);
        return;
      }
      
      try {
        const [currentRes, historyRes] = await Promise.all([
          axios.get(`${API_BASE}/governance/baselines/current/${scopeId}`, { headers: getAuthHeaders() }),
          axios.get(`${API_BASE}/governance/baselines/history/${scopeId}`, { headers: getAuthHeaders() })
        ]);
        
        setCurrentBaseline(currentRes.data.baseline);
        setHistory(historyRes.data.history || []);
      } catch (err) {
        console.error('Failed to load baseline data:', err);
        toast.error('Failed to load baseline data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [scopeId]);
  
  // Compare baseline with proposed changes
  const handleCompare = async () => {
    if (proposedChanges.length === 0) {
      toast.error('Please add at least one proposed change');
      return;
    }
    if (!changeReason) {
      toast.error('Please provide a reason for the changes');
      return;
    }
    
    setComparing(true);
    try {
      const res = await axios.post(
        `${API_BASE}/governance/baselines/compare`,
        {
          scope_id: scopeId,
          proposed_changes: proposedChanges,
          change_reason: changeReason
        },
        { headers: getAuthHeaders() }
      );
      setComparison(res.data.comparison);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to compare baselines');
    } finally {
      setComparing(false);
    }
  };
  
  // Approve and create vNext
  const handleApproveVNext = async () => {
    if (!comparison) return;
    
    setApproving(true);
    try {
      const res = await axios.post(
        `${API_BASE}/governance/baselines/approve-vnext`,
        {
          scope_id: scopeId,
          approval_notes: changeReason,
          proposed_total_cost: comparison.proposed.total_cost,
          proposed_capex: comparison.proposed.total_capex,
          proposed_opex: comparison.proposed.total_opex,
          proposed_labor: comparison.proposed.total_labor,
          proposed_non_labor: comparison.proposed.total_non_labor,
          proposed_benefits: currentBaseline?.total_financial_benefit || 0,
          change_reason: changeReason
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success(`Baseline V${res.data.version} created successfully!`);
      
      // Reload data
      const [currentRes, historyRes] = await Promise.all([
        axios.get(`${API_BASE}/governance/baselines/current/${scopeId}`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/governance/baselines/history/${scopeId}`, { headers: getAuthHeaders() })
      ]);
      
      setCurrentBaseline(currentRes.data.baseline);
      setHistory(historyRes.data.history || []);
      setComparison(null);
      setProposedChanges([]);
      setChangeReason('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create vNext baseline');
    } finally {
      setApproving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }
  
  if (!scopeId) {
    return (
      <div className="p-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
          <h2 className="text-lg font-semibold text-amber-800">No Scope Selected</h2>
          <p className="text-amber-600 mt-2">Please select a scope to compare baselines.</p>
          <button
            onClick={() => navigate('/financial')}
            className="mt-4 px-4 py-2 bg-amber-600 text-white rounded font-medium hover:bg-amber-700"
          >
            Go to Financial Module
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-100 p-6" data-testid="rebaseline-comparison">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Re-Baseline Comparison</h1>
        <p className="text-gray-500">Compare current baseline with proposed changes</p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Current Baseline & History */}
        <div className="space-y-6">
          {/* Current Baseline */}
          {currentBaseline ? (
            <div className="bg-white rounded-lg border p-4">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Current Baseline (V{currentBaseline.version})
              </h3>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Cost</span>
                  <span className="font-semibold">{formatCurrency(currentBaseline.total_cost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">CAPEX</span>
                  <span>{formatCurrency(currentBaseline.total_capex)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">OPEX</span>
                  <span>{formatCurrency(currentBaseline.total_opex)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Labor</span>
                  <span>{formatCurrency(currentBaseline.total_labor)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Non-Labor</span>
                  <span>{formatCurrency(currentBaseline.total_non_labor)}</span>
                </div>
                <div className="border-t pt-3 flex justify-between">
                  <span className="text-gray-500">ROI</span>
                  <span className="font-semibold text-green-600">{currentBaseline.roi_percentage?.toFixed(1)}%</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg border-2 border-dashed p-6 text-center">
              <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No baseline exists for this scope yet.</p>
            </div>
          )}
          
          {/* History Timeline */}
          <BaselineTimeline history={history} formatCurrency={formatCurrency} />
        </div>
        
        {/* Middle Column - Proposed Changes */}
        <div className="space-y-6">
          <ProposedChangesForm
            changes={proposedChanges}
            onAddChange={(change) => setProposedChanges([...proposedChanges, change])}
            onRemoveChange={(idx) => setProposedChanges(proposedChanges.filter((_, i) => i !== idx))}
            formatCurrency={formatCurrency}
          />
          
          {/* Change Reason */}
          <div className="bg-white rounded-lg border p-4">
            <label className="text-sm font-medium text-gray-700">Change Reason *</label>
            <textarea
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              placeholder="Why are these changes needed?"
              className="w-full border rounded px-3 py-2 mt-2"
              rows={3}
            />
          </div>
          
          {/* Compare Button */}
          <button
            onClick={handleCompare}
            disabled={comparing || proposedChanges.length === 0 || !changeReason}
            className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {comparing ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <ArrowRight className="w-5 h-5" />
                Compare Changes
              </>
            )}
          </button>
        </div>
        
        {/* Right Column - Comparison Results */}
        <div className="space-y-6">
          {comparison ? (
            <>
              {/* Impact Severity */}
              <div className="bg-white rounded-lg border p-4 text-center">
                <h3 className="text-sm text-gray-500 mb-2">Impact Assessment</h3>
                <SeverityBadge severity={comparison.impact_severity} />
                <p className="mt-2 text-sm text-gray-600">
                  {comparison.requires_approval ? 'Executive approval recommended' : 'Standard approval process'}
                </p>
              </div>
              
              {/* Delta Comparisons */}
              <DeltaDisplay
                label="Total Cost"
                current={comparison.current_baseline.total_cost}
                proposed={comparison.proposed.total_cost}
                formatCurrency={formatCurrency}
              />
              <DeltaDisplay
                label="CAPEX"
                current={comparison.current_baseline.total_capex}
                proposed={comparison.proposed.total_capex}
                formatCurrency={formatCurrency}
              />
              <DeltaDisplay
                label="OPEX"
                current={comparison.current_baseline.total_opex}
                proposed={comparison.proposed.total_opex}
                formatCurrency={formatCurrency}
              />
              <DeltaDisplay
                label="Labor"
                current={comparison.current_baseline.total_labor}
                proposed={comparison.proposed.total_labor}
                formatCurrency={formatCurrency}
              />
              
              {/* Summary */}
              <div className="bg-white rounded-lg border p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Total Variance</span>
                    <span className={comparison.deltas.total_cost >= 0 ? 'text-red-600' : 'text-green-600'}>
                      {comparison.deltas.total_cost >= 0 ? '+' : ''}{formatCurrency(comparison.deltas.total_cost)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Variance %</span>
                    <span className={comparison.deltas.variance_percentage >= 0 ? 'text-red-600' : 'text-green-600'}>
                      {comparison.deltas.variance_percentage >= 0 ? '+' : ''}{comparison.deltas.variance_percentage}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Next Version</span>
                    <span className="font-semibold">V{comparison.next_version}</span>
                  </div>
                </div>
              </div>
              
              {/* Approve Button */}
              <button
                onClick={handleApproveVNext}
                disabled={approving}
                className="w-full py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {approving ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Approve & Create V{comparison.next_version}
                  </>
                )}
              </button>
            </>
          ) : (
            <div className="bg-gray-50 rounded-lg border-2 border-dashed p-8 text-center">
              <ArrowRight className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">Add proposed changes and click "Compare" to see the impact analysis.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReBaselineComparison;
